// ---------------------------------------------------------------------------
// Tabs. Enhances any `[data-tabs]` container whose buttons carry
// `data-tab-target="<panelId>"`. The active tab is restored from the URL hash,
// then sessionStorage (so it survives the POST→redirect→GET reloads the catalog
// forms do), then defaults to the first tab. Without JS all panels stay visible.
// ---------------------------------------------------------------------------

function storageKey(container: HTMLElement): string {
  const id = container.dataset.tabs || "tabs";
  return `connectpay:tab:${location.pathname}:${id}`;
}

function activate(
  container: HTMLElement,
  tabs: HTMLElement[],
  target: string,
  persist: boolean,
): void {
  let matched = false;
  for (const tab of tabs) {
    const isActive = tab.dataset.tabTarget === target;
    tab.setAttribute("aria-selected", String(isActive));
    tab.tabIndex = isActive ? 0 : -1;

    const panel = document.getElementById(tab.dataset.tabTarget ?? "");
    if (panel) panel.hidden = !isActive;
    if (isActive) matched = true;
  }
  if (matched && persist) {
    try {
      sessionStorage.setItem(storageKey(container), target);
    } catch {
      /* storage may be unavailable (private mode) — non-fatal */
    }
  }
}

function initContainer(container: HTMLElement): void {
  const tabs = Array.from(
    container.querySelectorAll<HTMLElement>("[data-tab-target]"),
  );
  if (tabs.length === 0) return;

  const ids = tabs.map((t) => t.dataset.tabTarget);
  const hashId = location.hash.slice(1);
  let stored: string | null = null;
  try {
    stored = sessionStorage.getItem(storageKey(container));
  } catch {
    stored = null;
  }

  const initial =
    (hashId && ids.includes(hashId) && hashId) ||
    (stored && ids.includes(stored) && stored) ||
    tabs[0].dataset.tabTarget ||
    "";

  activate(container, tabs, initial, false);
  container.dataset.ready = "true";

  container.addEventListener("click", (event) => {
    const tab = (event.target as HTMLElement).closest<HTMLElement>(
      "[data-tab-target]",
    );
    if (!tab || !container.contains(tab)) return;
    event.preventDefault();
    activate(container, tabs, tab.dataset.tabTarget ?? "", true);
  });

  // Left/right arrow navigation within the tablist.
  container.addEventListener("keydown", (event) => {
    if (event.key !== "ArrowRight" && event.key !== "ArrowLeft") return;
    const current = tabs.findIndex(
      (t) => t.getAttribute("aria-selected") === "true",
    );
    if (current === -1) return;
    const delta = event.key === "ArrowRight" ? 1 : -1;
    const next = tabs[(current + delta + tabs.length) % tabs.length];
    activate(container, tabs, next.dataset.tabTarget ?? "", true);
    next.focus();
  });
}

export function initTabs(): void {
  document
    .querySelectorAll<HTMLElement>("[data-tabs]")
    .forEach(initContainer);
}
